import { createClient, fetchExchange, gql } from "urql";
import { env } from "../config/env";
import { PracticePartName } from "../types/micropractice";

export type PublicPracticePart = {
  part: PracticePartName;
  label?: string | null;
  title: string;
  body: string;
  page_number: number;
  pdf_url: string;
  preview_url?: string | null;
  download_path: string;
};

export type TeachingMetadataItem = { id: string; name: string; sno: number };
export type PublicMicropractice = {
  id: string;
  slug: string;
  title: string;
  description: string;
  pillar_id: string;
  category_id: string;
  pdf_url: string;
  thumbnail_url?: string | null;
  preview_pdf_url?: string | null;
  preview_image_url?: string | null;
  thumbnail_aspect_ratio?: number | null;
  page_count: number;
  publish_date: string;
  publish_timezone: string;
  download_path: string;
  metadata_pillar: TeachingMetadataItem;
  metadata_micropractice_category: TeachingMetadataItem;
};
export type PublicMicropracticeDetail = PublicMicropractice & {
  parts: PublicPracticePart[];
};
export type TeachingLibrary = {
  teaching_metadata: {
    pillars: TeachingMetadataItem[];
    categories: TeachingMetadataItem[];
  };
  teaching_micropractices: {
    items: PublicMicropractice[];
    featured: PublicMicropractice[];
    // All matching published practices; the separate weekly spotlight is not paginated.
    total: number;
  };
};
export type TeachingPage =
  | "teaching"
  | "student_curriculum"
  | "core_tools"
  | "micropractices"
  | "micropractice_detail"
  | "resources";

export const teachingClient = createClient({
  url: new URL("/v1/teaching/graphql", String(env.apiURL)).href,
  exchanges: [fetchExchange],
  requestPolicy: "network-only",
  fetchOptions: {
    credentials: "omit",
    referrerPolicy: "no-referrer",
    cache: "no-store",
  },
});
export const TEACHING_LIBRARY = gql`
  query TeachingLibrary($params: MicropracticeListInput!) {
    teaching_micropractices(params: $params) {
      total
      items {
        ...PracticeCard
      }
      featured {
        ...PracticeCard
      }
    }
  }
  fragment PracticeCard on Micropractice {
    id
    slug
    title
    description
    pillar_id
    category_id
    pdf_url
    thumbnail_url
    preview_pdf_url
    preview_image_url
    thumbnail_aspect_ratio
    page_count
    publish_date
    publish_timezone
    download_path
    metadata_pillar {
      id
      name
      sno
    }
    metadata_micropractice_category {
      id
      name
      sno
    }
  }
`;
export const TEACHING_PRACTICE = gql`
  query TeachingPractice($slug: String!) {
    micropractice(slug: $slug) {
      id
      slug
      title
      description
      pillar_id
      category_id
      pdf_url
      thumbnail_url
      thumbnail_aspect_ratio
      page_count
      publish_date
      publish_timezone
      download_path
      parts {
        part
        label
        preview_url
        title
        body
        page_number
        pdf_url
        download_path
      }
      metadata_pillar {
        id
        name
        sno
      }
      metadata_micropractice_category {
        id
        name
        sno
      }
    }
  }
`;
export const TEACHING_METADATA = gql`
  query TeachingMetadata {
    teaching_metadata {
      pillars {
        id
        name
        sno
      }
      categories {
        id
        name
        sno
      }
    }
  }
`;
const VIEW = gql`
  mutation TeachingView($page: TeachingPage!, $id: ID, $event: ID!) {
    record_teaching_view(page: $page, micropractice_id: $id, event_id: $event) {
      message
    }
  }
`;
const DOWNLOAD = gql`
  mutation TeachingDownload($page: TeachingPage!, $event: ID!) {
    record_teaching_download(page: $page, event_id: $event) {
      message
    }
  }
`;
async function recordTeachingEvent(
  query: typeof VIEW,
  variables: Record<string, unknown>,
) {
  // A new random ID for this event only, never a visitor/device/session ID.
  // Keep it in memory across retries so an accepted request with a lost response
  // cannot increment the count twice. No cookies or browser storage are used.
  const params = { ...variables, event: crypto.randomUUID() };
  for (let attempt = 0; attempt < 3; attempt++) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 12000);
    try {
      const result = await teachingClient
        .mutation(query, params, {
          fetchOptions: {
            credentials: "omit",
            referrerPolicy: "no-referrer",
            keepalive: true,
            signal: controller.signal,
          },
        })
        .toPromise();
      const field =
        query === VIEW ? "record_teaching_view" : "record_teaching_download";
      if (!result.error && result.data?.[field]) return;
    } catch {
      // Retry network failures without interrupting the visitor's navigation.
    } finally {
      clearTimeout(timeout);
    }
    if (attempt < 2)
      await new Promise<void>((resolve) =>
        setTimeout(resolve, 500 * 2 ** attempt),
      );
  }
  // Never log URLs, response payloads, or visitor information on public pages.
  console.warn("Teaching analytics could not be recorded after retries.");
}
export const recordTeachingView = (page: TeachingPage, id?: string) =>
  recordTeachingEvent(VIEW, { page, id });
export const recordTeachingDownload = (page: TeachingPage) => {
  void recordTeachingEvent(DOWNLOAD, { page });
};
export const practiceDownloadURL = (practice: PublicMicropractice) =>
  new URL(
    `/v1/teaching/micropractices/${encodeURIComponent(practice.slug)}/download`,
    String(env.apiURL),
  ).href;
export const practicePartDownloadURL = (
  practice: PublicMicropractice,
  part: PublicPracticePart,
) =>
  new URL(
    `/v1/teaching/micropractices/${encodeURIComponent(practice.slug)}/parts/${encodeURIComponent(part.part)}/download`,
    String(env.apiURL),
  ).href;
